import { z } from 'zod';

/**
 * Zod schemas for compliance-related entities
 * These provide runtime validation and type inference
 */

// HCVProgram schema
export const hcvProgramSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  unitId: z.string(),
  tenantId: z.string(),
  leaseId: z.string().nullish(),
  phaName: z.string().min(1, 'PHA name is required'),
  voucherNumber: z.string().min(1, 'Voucher number is required'),
  contractRent: z.number().nonnegative(),
  tenantPortion: z.number().nonnegative(),
  phaPortion: z.number().nonnegative(),
  inspectionStatus: z.enum(['Passed', 'Failed', 'Pending', 'Scheduled'] as const),
  nextInspectionDate: z.string(),
  complianceStatus: z.enum(['Compliant', 'At Risk', 'Non-Compliant'] as const),
  notes: z.string().optional(),
});

export type HCVProgram = z.infer<typeof hcvProgramSchema>;

// ComplianceDocument schema
export const complianceDocumentSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Document name is required'),
  category: z.enum(['Certificate', 'Permit', 'License', 'Insurance', 'Notice', 'Attestation'] as const),
  propertyId: z.string(),
  propertyName: z.string().optional(),
  uploadedBy: z.string(),
  uploadedDate: z.string(),
  expirationDate: z.string().optional(), // Optional - some documents may not expire
  status: z.enum(['Current', 'Expiring Soon', 'Expired'] as const),
  daysUntilExpiration: z.number().int().optional(), // Optional - calculated field
  url: z.string().optional(), // Optional - allow relative paths or empty strings
  hcvProgramId: z.string().optional(),
  fileSize: z.string().optional(),
  mimeType: z.string().optional(),
});

export type ComplianceDocument = z.infer<typeof complianceDocumentSchema>;

export const rentersInsuranceRecordSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  leaseId: z.string().nullish(),
  documentUrl: z.string().nullish(),
  expirationDate: z.string(),
  status: z.enum(['Current', 'Expiring Soon', 'Expired'] as const),
  daysUntilExpiration: z.number().int().nullable().optional(),
  uploadedAt: z.string(),
  uploadedBy: z.string(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type RentersInsuranceRecordDto = z.infer<typeof rentersInsuranceRecordSchema>;
